import { EMPTY_VALUE } from '@/constants/app.constants';
import { formatDisplayDate } from '@/utils/date';
import { formatTaka } from '@/utils/number';
import { PCMP_CHANNEL_LABELS } from '../constants/pcmpProposal.constants';
import type { PcmpProposalStudentClass } from '../types/pcmpProposal.types';

// Renders an edit event's `changes` ({ field: { from, to } }, API LB-744/745)
// as readable lines. Keys the page doesn't know fall back to the raw key.

export interface PcmpChangeLine {
  key: string;
  label: string;
  from: string;
  to: string;
}

const FIELD_LABELS: Record<string, string> = {
  instituteId: 'Institute',
  comments: 'Note',
  name: 'Teacher name',
  mobile: 'Teacher mobile',
  designation: 'Designation',
  subject: 'Subject',
  agreedAmount: 'Agreed amount',
  proposedAmount: 'Proposed amount',
  isOnBehalf: 'On behalf',
  onBehalfName: 'On behalf of',
  paymentDate: 'Payment date',
  referenceNo: 'Reference no',
  paymentSource: 'Payment source',
  // A rejected Proposal Correction request's rollback (API LB-1194).
  teacherId: 'Linked teacher',
  paymentAccountId: 'Payment account',
  paymentDay: 'Payment date',
  'submission.instituteId': 'Institute',
  'submission.comments': 'Note',
  'account.kind': 'Account type',
  'account.bankName': 'Bank',
  'account.branchName': 'Branch',
  'account.accountNo': 'Account no',
  'account.routingNo': 'Routing no',
  'account.mobileNo': 'Wallet number',
  'account.holderName': 'Account holder',
};

const MONEY_FIELDS = new Set(['agreedAmount', 'proposedAmount']);
const DATE_FIELDS = new Set(['paymentDate', 'paymentDay']);

// The rollback of a rejected correction names the teacher and proposal rows it
// restores (`teachers.12.name`, `proposals.7.isOnBehalf`); the field is the rest.
const ROW_FIELD_KEY = /^(?:teachers|proposals)\.\d+\.(\w+)$/;

/** The field a change key names, without the row it belongs to. */
function fieldOf(key: string): string {
  return ROW_FIELD_KEY.exec(key)?.[1] ?? key;
}

const CLASS_QTY_KEY = /^classes\.(\d+)\.studentQty$/;
const BOOK_QTY_KEY = /^books\.(\d+)\.tentativePurchaseQty$/;

interface ChangeContext {
  students: PcmpProposalStudentClass[];
}

function labelFor(key: string, { students }: ChangeContext): string {
  const classMatch = CLASS_QTY_KEY.exec(key);
  if (classMatch) {
    const row = students.find((s) => s.classId === Number(classMatch[1]));
    return `${row?.className ?? `Class #${classMatch[1]}`} students`;
  }
  const bookMatch = BOOK_QTY_KEY.exec(key);
  if (bookMatch) {
    const bookId = Number(bookMatch[1]);
    for (const row of students) {
      const book = row.books.find((b) => b.projectBookId === bookId);
      if (book) return `${row.className ?? `Class #${row.classId}`} · ${book.categoryName} qty`;
    }
    return `Book #${bookId} qty`;
  }
  return FIELD_LABELS[key] ?? FIELD_LABELS[fieldOf(key)] ?? key;
}

function formatValue(rawKey: string, value: unknown): string {
  const key = fieldOf(rawKey);
  if (value === null || value === undefined || value === '') return EMPTY_VALUE;
  if (Array.isArray(value)) return value.map((v) => formatValue(key, v)).join(' / ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return MONEY_FIELDS.has(key) ? formatTaka(value) : String(value);
  if (typeof value === 'string') {
    // The rollback carries money as the text the database prints.
    if (MONEY_FIELDS.has(key) && value.trim() !== '' && Number.isFinite(Number(value))) {
      return formatTaka(Number(value));
    }
    if (key === 'paymentSource' && value in PCMP_CHANNEL_LABELS) {
      return PCMP_CHANNEL_LABELS[value as keyof typeof PCMP_CHANNEL_LABELS];
    }
    if (DATE_FIELDS.has(key)) return formatDisplayDate(value);
    return value;
  }
  return JSON.stringify(value);
}

function isFromTo(value: unknown): value is { from: unknown; to: unknown } {
  return typeof value === 'object' && value !== null && 'from' in value && 'to' in value;
}

export function toPcmpChangeLines(
  changes: Record<string, unknown> | null,
  context: ChangeContext,
): PcmpChangeLine[] {
  if (!changes) return [];
  return Object.entries(changes).map(([key, change]) => ({
    key,
    label: labelFor(key, context),
    from: isFromTo(change) ? formatValue(key, change.from) : EMPTY_VALUE,
    to: formatValue(key, isFromTo(change) ? change.to : change),
  }));
}
